import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Calendar, MapPin, Tag } from "lucide-react";
import api from "@/lib/api";
import NavBar from "@/components/NavBar";
import Seo from "@/components/Seo";
import Footer from "@/components/Footer";
import { FadeUp } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";

const CAT_LABEL = {
  concert: { zh: "演唱会", en: "CONCERT" },
  festival: { zh: "音乐节", en: "FESTIVAL" },
  corporate: { zh: "商业活动", en: "CORPORATE" },
  production: { zh: "音乐制作", en: "PRODUCTION" },
};

export default function ProjectDetail() {
  const { id } = useParams();
  const { lang } = useLang();
  const { data: p, isLoading } = useQuery({
    queryKey: ["project", id],
    queryFn: () => api.get(`/projects/${id}`).then((r) => r.data),
    retry: 1,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="font-mono2 text-xs text-[#a1a1aa] tracking-[0.3em]">LOADING…</p>
      </div>
    );
  }

  if (!p) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center gap-6">
        <p className="font-display text-3xl text-white uppercase">{lang === "zh" ? "找不到该项目" : "Project not found"}</p>
        <a href="/" className="btn-yellow">
          <ArrowLeft size={14} /> {lang === "zh" ? "返回首页" : "BACK HOME"}
        </a>
      </div>
    );
  }

  const cat = CAT_LABEL[p.category] || {};
  const zh = lang === "zh";

  return (
    <div className="bg-[#050505] min-h-screen" data-testid="project-detail">
      <Seo title={`${p.title} — DFE 娛樂夢工廠`} description={zh ? `${p.year || ""} ${p.location} ${p.role}`.trim() : `${p.title} — ${p.role}`} />
      <NavBar />
      <main className="pt-16">
        <section className="relative h-[52vh] md:h-[70vh] overflow-hidden">
          {p.image && <img src={p.image} alt={p.title} className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-black/30 to-black/50" />
          <div className="absolute inset-x-0 bottom-0 max-w-[1800px] mx-auto px-4 md:px-8 pb-10">
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
              <div className="font-mono2 text-xs text-[#ffde00] tracking-[0.3em] mb-3">
                {p.year || "—"} · {p.location || (zh ? cat.zh : cat.en)} · {zh ? cat.zh : cat.en}
              </div>
              <h1 className="font-display text-4xl md:text-6xl lg:text-7xl uppercase text-[#f5f5f5] leading-[0.95]">{p.title}</h1>
            </motion.div>
          </div>
        </section>

        <section className="max-w-[1800px] mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            <FadeUp className="md:col-span-5">
              <div className="border border-white/10 bg-[#0a0a0a] divide-y divide-white/10">
                {[
                  { icon: Calendar, label: zh ? "年份" : "YEAR", value: p.year || "—" },
                  { icon: MapPin, label: zh ? "地点" : "LOCATION", value: p.location || "—" },
                  { icon: Tag, label: zh ? "类型" : "TYPE", value: zh ? cat.zh : cat.en || "—" },
                ].map((r) => (
                  <div key={r.label} className="flex items-center gap-4 px-6 py-5">
                    <r.icon size={18} className="text-[#ffde00]" />
                    <span className="font-mono2 text-[10px] text-[#52525b] tracking-[0.3em] w-24">{r.label}</span>
                    <span className="font-zh text-sm text-[#f5f5f5]">{r.value}</span>
                  </div>
                ))}
                {p.role && (
                  <div className="px-6 py-5">
                    <div className="font-mono2 text-[10px] text-[#52525b] tracking-[0.3em] mb-2">{zh ? "DFE 角色" : "DFE ROLE"}</div>
                    <p className="font-zh text-sm text-[#a1a1aa] leading-relaxed">{p.role}</p>
                  </div>
                )}
              </div>
              <a href="/#contact" className="btn-yellow w-full justify-center mt-8">
                {zh ? "洽谈同类合作" : "ENQUIRE FOR SIMILAR SHOWS"}
                <ArrowUpRight size={15} />
              </a>
            </FadeUp>

            <div className="md:col-span-7">
              {p.lineup && (
                <FadeUp>
                  <div className="font-mono2 text-xs text-[#ffde00] tracking-[0.3em] mb-3">{zh ? "阵容 LINE-UP" : "LINE-UP"}</div>
                  <p className="font-zh text-lg md:text-2xl text-[#f5f5f5] leading-relaxed whitespace-pre-line">{p.lineup}</p>
                </FadeUp>
              )}
              {p.details && (
                <FadeUp delay={0.1} className={p.lineup ? "mt-10" : ""}>
                  <div className="font-mono2 text-xs text-[#ffde00] tracking-[0.3em] mb-3">{zh ? "制作说明 PRODUCTION" : "PRODUCTION NOTES"}</div>
                  <p className="font-zh text-base text-[#a1a1aa] leading-relaxed whitespace-pre-line">{p.details}</p>
                </FadeUp>
              )}
              {!p.lineup && !p.details && (
                <FadeUp>
                  <p className="font-zh text-base text-[#a1a1aa] leading-relaxed">{p.role}</p>
                </FadeUp>
              )}
            </div>
          </div>

          {p.gallery && p.gallery.length > 0 && (
            <FadeUp className="mt-16 md:mt-24">
              <div className="font-mono2 text-xs text-[#ffde00] tracking-[0.3em] mb-6 border-b border-white/10 pb-4">
                {zh ? "现场图集 GALLERY" : "GALLERY"}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {p.gallery.map((g, i) => (
                  <div key={i} className={`overflow-hidden border border-white/10 ${i % 3 === 0 ? "md:col-span-2" : ""}`}>
                    <img
                      src={g}
                      alt={`${p.title} — ${i + 1}`}
                      loading="lazy"
                      className="w-full h-[240px] md:h-[420px] object-cover hover:scale-105 transition duration-700 ease-out"
                    />
                  </div>
                ))}
              </div>
            </FadeUp>
          )}

          <FadeUp className="mt-16 border-t border-white/10 pt-8">
            <a href="/#works" data-testid="detail-back" className="inline-flex items-center gap-2 font-mono2 text-xs text-[#a1a1aa] hover:text-[#ffde00] tracking-[0.25em] transition-colors">
              <ArrowLeft size={14} /> {zh ? "返回全部作品" : "BACK TO ALL WORKS"}
            </a>
          </FadeUp>
        </section>
      </main>
      <Footer />
    </div>
  );
}
